import React, { useState, useCallback, Suspense, lazy } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DialogTransition from './Common/DialogTransition';

const PROVIDER_OPTIONS = [
  {
    id: 'oidc',
    title: 'OpenID Connect v1.0',
  },
  {
    id: 'saml',
    title: 'SAML v2.0',
  },
];

const OpenIDConnectForm = lazy(() => import('./forms/OpenIDConnectForm'));
const SAMLForm = lazy(() => import('./forms/SAMLForm'));

/**
 * Loading fallback component for lazy loaded forms
 */
const FormLoadingFallback = () => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    minHeight="200px"
    gap={2}
  >
    <CircularProgress size={32} />
    <Typography variant="body2" color="text.secondary">
      Loading form...
    </Typography>
  </Box>
);

/**
 * Unified Identity Provider Dialog
 */
const IdentityProviderDialog = ({
  open,
  mode = 'create',
  currentIdentityProviderDetail,
  organizationID,
  onClose,
  createIdentityProviderOidc,
  updateIdentityProviderOidc,
  createIdentityProviderSaml,
  updateIdentityProviderSaml,
  onSuccess,
}) => {
  const [selectedProvider, setSelectedProvider] = useState('oidc');
  const formRef = React.useRef(null);

  React.useEffect(() => {
    if (mode === 'edit' && currentIdentityProviderDetail?.provider_id) {
      const providerType =
        currentIdentityProviderDetail.provider_id === 'OIDC' ? 'oidc' : 'saml';
      setSelectedProvider(providerType);
    } else if (mode === 'create') {
      setSelectedProvider('oidc');
    }
  }, [mode, currentIdentityProviderDetail, open]);

  const handleProviderChange = useCallback((providerType) => {
    setSelectedProvider(providerType);
  }, []);

  const handleFormClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    if (formRef.current && formRef.current.submitForm) {
      formRef.current.submitForm();
    }
  }, []);

  const handleReset = useCallback(() => {
    if (formRef.current && formRef.current.resetForm) {
      formRef.current.resetForm();
      if (mode === 'create') {
        setSelectedProvider('oidc');
      }
    }
  }, [mode]);

  // Render the appropriate form based on selected provider
  const renderForm = useCallback(() => {
    const commonProps = {
      ref: formRef,
      mode,
      currentIdentityProviderDetail,
      organizationID,
      onClose: handleFormClose,
      onSuccess,
      showProviderDropdown: mode === 'create',
      selectedProvider,
      onProviderChange: handleProviderChange,
      providersOptions: PROVIDER_OPTIONS,
    };

    switch (selectedProvider) {
      case 'oidc':
        return (
          <OpenIDConnectForm
            {...commonProps}
            createIdentityProviderOidc={createIdentityProviderOidc}
            updateIdentityProviderOidc={updateIdentityProviderOidc}
          />
        );
      case 'saml':
        return (
          <SAMLForm
            {...commonProps}
            createIdentityProviderSaml={createIdentityProviderSaml}
            updateIdentityProviderSaml={updateIdentityProviderSaml}
          />
        );
      default:
        return (
          <OpenIDConnectForm
            {...commonProps}
            createIdentityProviderOidc={createIdentityProviderOidc}
            updateIdentityProviderOidc={updateIdentityProviderOidc}
          />
        );
    }
  }, [
    mode,
    currentIdentityProviderDetail,
    organizationID,
    handleFormClose,
    onSuccess,
    selectedProvider,
    handleProviderChange,
    createIdentityProviderOidc,
    updateIdentityProviderOidc,
    createIdentityProviderSaml,
    updateIdentityProviderSaml,
  ]);

  return (
    <Dialog
      maxWidth="xs"
      fullWidth
      open={open}
      onClose={onClose}
      TransitionComponent={DialogTransition}
    >
      <DialogTitle>
        {mode === 'edit'
          ? 'Edit Identity Provider'
          : 'Create Identity Provider'}
        <IconButton
          color="primary"
          aria-label="Close dialog"
          sx={{ position: 'absolute', right: 16, top: 8 }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Suspense fallback={<FormLoadingFallback />}>{renderForm()}</Suspense>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={handleReset}>
          Reset
        </Button>
        <Button variant="contained" onClick={handleSubmit}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IdentityProviderDialog;
